import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Public endpoints that don't need auth tokens
const PUBLIC_PATHS = ['/products', '/cultures', '/vendors', '/payment/methods', '/health'];

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || localStorage.getItem('shopperToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    const isPublic = PUBLIC_PATHS.some(p => config.url?.startsWith(p));
    if (!isPublic) {
      console.warn('No auth token for request to:', config.url);
    }
  }
  return config;
});

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (formData) => api.post('/products', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const updateProduct = (id, formData) => api.put(`/products/${id}`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const toggleProductStatus = (id) => api.patch(`/products/${id}/toggle-active`);
export const toggleProductVerified = (id) => api.patch(`/products/${id}/toggle-verified`);
export const toggleProductFeatured = (id) => api.patch(`/products/${id}/toggle-featured`);
export const reviewProduct = (id) => api.patch(`/products/${id}/review`);
export const verifyProduct = (id) => api.patch(`/products/${id}/verify`);
export const publishProduct = (id, publish) => api.patch(`/products/${id}/publish`, { publish });
export const updateProductStock = (id, stockQuantity) => api.patch(`/products/${id}/stock`, { stockQuantity });
export const decrementProductStock = (id, quantity, orderId) => api.patch(`/products/${id}/stock`, { change: -Math.abs(quantity), orderId });
export const updateProductImage = (id, formData) => api.patch(`/products/${id}/image`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteProductImage = (id, imageIndex) => api.delete(`/products/${id}/image/${imageIndex}`);
export const downloadProductsTemplate = () => api.get('/products/excel/template', { responseType: 'blob' });
export const exportProducts = () => api.get('/products/excel/export', { responseType: 'blob' });
export const importProducts = (formData) => api.post('/products/excel/import', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Orders
export const createOrder = (orderData) => api.post('/orders', orderData);
export const getOrders = (params) => api.get('/orders', { params });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const getOrderByNumber = (orderNumber) => api.get(`/orders/number/${orderNumber}`);
export const updateOrderStatus = (id, statusData) => api.patch(`/orders/${id}/status`, statusData);
export const deleteOrder = (id) => api.delete(`/orders/${id}`, { responseType: 'blob' });
export const calculateShipping = (countryCode) => api.post('/orders/calculate-shipping', { countryCode });

// Vendors
export const submitVendorApplication = (data) => api.post('/vendors', data);
export const getVendorApplications = (params) => api.get('/vendors', { params });
export const getVendorApplication = (id) => api.get(`/vendors/${id}`);
export const getVendorApplicationDetails = (id) => api.get(`/vendors/${id}/details`);
export const updateVendorStatus = (id, data) => api.patch(`/vendors/${id}/status`, data);
export const deleteVendorApplication = (id) => api.delete(`/vendors/${id}`, { responseType: 'blob' });
export const downloadVendorApplication = (id) => api.get(`/vendors/${id}/download`);

// Payment
// The payable amount is recomputed server-side from `items` (cart product
// ids/quantities) against authoritative catalog prices — the backend no
// longer trusts a client-supplied amount.
export const generateYocoCheckout = (orderId, customer, orderNumber, items) => api.post('/payment/yoco/create-checkout', { orderId, orderNumber, customerName: customer.name, customerEmail: customer.email, customerPhone: customer.phone, items });
export const getPaymentMethods = () => api.get('/payment/methods');
export const sendAdminOrderNotification = (orderData) => api.post('/orders/send-admin-notification', orderData);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (userData) => api.post('/auth/register', userData);
export const googleLogin = (googleToken) => api.post('/auth/google', { googleToken });
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const shopperLogin = (firebaseIdToken, shopperInfo) => api.post('/auth/shopper/firebase', {
  firebaseIdToken,
  uid: shopperInfo?.uid,
  email: shopperInfo?.email,
  displayName: shopperInfo?.name || shopperInfo?.displayName
});

// User Profile
export const getUser = () => api.get('/user/me');
export const updateProfile = (userData) => api.put('/user/me', userData);
export const getAddresses = () => api.get('/user/addresses');
export const addAddress = (addressData) => api.post('/user/addresses', addressData);
export const updateAddress = (addressId, addressData) => api.put(`/user/addresses/${addressId}`, addressData);
export const deleteAddress = (addressId) => api.delete(`/user/addresses/${addressId}`);
export const getUserPaymentMethods = () => api.get('/user/payment-methods');
export const addPaymentMethod = (methodData) => api.post('/user/payment-methods', methodData);
export const updatePaymentMethod = (methodId, methodData) => api.put(`/user/payment-methods/${methodId}`, methodData);
export const deletePaymentMethod = (methodId) => api.delete(`/user/payment-methods/${methodId}`);

// Cultures
export const getCultures = (params) => api.get('/cultures', { params });
export const getCultureNames = () => api.get('/cultures/names');
export const getCulture = (cultureName) => api.get(`/cultures/${cultureName}`);
export const createCulture = (cultureData) => api.post('/cultures', cultureData);
export const updateCulture = (id, cultureData) => api.put(`/cultures/${id}`, cultureData);
export const deleteCulture = (id) => api.delete(`/cultures/${id}`);
export const toggleCultureFeatured = (id) => api.patch(`/cultures/${id}/featured`);
export const searchCultures = (query) => api.post('/cultures/search', { query });

// Product Reviews
export const getProductReviews = (productId) => api.get(`/products/${productId}/reviews`);
export const submitProductReview = (productId, data) => api.post(`/products/${productId}/reviews`, data);

export default api;
